/**
 * PartnerOnboardingPage — /partner/onboarding
 *
 * Phase H Step 1. Renders the 7-state identity-verification funnel:
 *   verification_pending → identity_in_progress → identity_completed
 *   → admin_in_review → (needs_info | approved | rejected)
 *
 * Copy discipline (Family H): the ONLY permitted verification claims
 * are "Identity-verified via Stripe" and "Manually reviewed by our
 * team". No implied background checks anywhere.
 *
 * The partner-facing state model:
 *   verification_pending   → CTA "Start identity verification"
 *                            (POST /partner/verification/start → transitions
 *                            to identity_in_progress, returns hosted_url).
 *   identity_in_progress   → open the hosted Stripe link; a Refresh
 *                            button polls check_status via
 *                            POST /partner/verification/{id}/refresh.
 *                            While `PP_STRIPE_STUB=1` (all preview envs
 *                            + pytest), the hosted link is a self-hosted
 *                            stub page (`?stub_verify=1`) that lets the
 *                            partner report stub_success / stub_failure —
 *                            same endpoint, deterministic path.
 *   identity_completed     → informational; waiting on admin pick-up.
 *   admin_in_review        → informational; reviewer is looking now.
 *   needs_info             → surfaces `needs_info_message`; CTA lets
 *                            the partner restart the identity check.
 *   approved               → celebration + Continue to dashboard.
 *   rejected               → surfaces reason + support email.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Loader2,
  ExternalLink,
  RefreshCw,
  Mail,
  ArrowRight,
  CheckCircle2,
  Clock,
  Info,
  XCircle,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PartnerPayoutSection from "@/components/PartnerPayoutSection";
import WelcomeToPackPopup from "@/components/WelcomeToPackPopup";

const STATE_LABEL = {
  verification_pending: "Ready to verify",
  identity_in_progress: "Identity verification in progress",
  identity_completed: "Awaiting manual review",
  admin_in_review: "Reviewer looking at your application",
  needs_info: "Reviewer needs more info",
  approved: "You're a Verified PooPartner!",
  rejected: "Application not approved",
};

const SUPPORT_EMAIL = "help@pooplucker.com";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
function StatusBadge({ status }) {
  const map = {
    verification_pending: { color: "#6b7280", bg: "#f3f4f6" },
    identity_in_progress: { color: "#7c3aed", bg: "#f5f3ff" },
    identity_completed: { color: "#0f766e", bg: "#ecfdf5" },
    admin_in_review: { color: "#b45309", bg: "#fffbeb" },
    needs_info: { color: "#b45309", bg: "#fffbeb" },
    approved: { color: "#065f46", bg: "#d1fae5" },
    rejected: { color: "#991b1b", bg: "#fee2e2" },
  };
  const p = map[status] || map.verification_pending;
  return (
    <span
      data-testid={`partner-onboarding-status-${status}`}
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        color: p.color,
        background: p.bg,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {STATE_LABEL[status] || status}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
export default function PartnerOnboardingPage() {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const stubVerify = searchParams.get("stub_verify") === "1";

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/verification/me");
      setRow(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Commit B — §4.3. Fires once on mount of the partner
  // onboarding funnel entry. Independent from loadMe so a
  // background refresh doesn't double-fire.
  useEffect(() => {
    trackEvent("partner.application_start");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startVerification = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/partner/verification/start");
      setRow(data.row);
      toast.success("Identity verification started.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const refreshStatus = async () => {
    if (!row?.id) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(
        `/partner/verification/${row.id}/refresh`,
      );
      setRow(data);
      if (data.status === "identity_completed") {
        toast.success("Identity verified. Reviewer will look next.");
      } else if (data.status === "verification_pending") {
        toast.error("Identity check didn't succeed. Please try again.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const container = useMemo(
    () => ({
      maxWidth: 720,
      margin: "0 auto",
      padding: "40px 20px 80px",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    }),
    [],
  );

  if (loading) {
    return (
      <div
        style={container}
        data-testid="partner-onboarding-loading"
      >
        <Loader2
          className="animate-spin"
          size={28}
          aria-label="Loading verification status"
        />
      </div>
    );
  }
  if (!row) return null;

  const inStubMode = row.status === "identity_in_progress" && (
    stubVerify || (row.hosted_url || "").includes("stripe.test")
  );

  return (
    <div
      style={container}
      data-testid="partner-onboarding-page"
    >
      <header style={{ marginBottom: 24 }}>
        <span
          style={{
            color: "#F89623",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 1.5,
            textTransform: "uppercase",
          }}
          data-testid="partner-onboarding-eyebrow"
        >
          PooPartner onboarding
        </span>
        <h1
          style={{ fontSize: 32, fontWeight: 800, margin: "8px 0 12px 0", color: "#374751" }}
          data-testid="partner-onboarding-heading"
        >
          Verify your identity
        </h1>
        <p
          style={{ color: "#4b5563", lineHeight: 1.6, marginBottom: 12 }}
          data-testid="partner-onboarding-subhead"
        >
          Two steps stand between you and your first booking:{" "}
          <strong>Identity-verified via Stripe</strong> and{" "}
          <strong>Manually reviewed by our team</strong>. We never ask for a
          social security number or an ID photo here — Stripe handles that
          securely on their side.
        </p>
        <StatusBadge status={row.status} />
      </header>

      {row.status === "verification_pending" && (
        <StepCard
          testId="partner-onboarding-step-pending"
          icon={<ShieldCheck size={22} />}
          title="Start identity verification"
          body={
            <p>
              You'll be sent to Stripe's secure identity flow to confirm who
              you are. It usually takes 3–5 minutes.
            </p>
          }
          cta={
            <Button
              onClick={startVerification}
              disabled={starting}
              data-testid="partner-onboarding-start-btn"
            >
              {starting ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                "Start identity verification"
              )}
              {!starting && <ArrowRight size={16} style={{ marginLeft: 8 }} />}
            </Button>
          }
        />
      )}

      {row.status === "identity_in_progress" && (
        <StepCard
          testId="partner-onboarding-step-in-progress"
          icon={<Loader2 size={22} />}
          title="Finish on Stripe, then come back"
          body={
            <div>
              <p style={{ marginBottom: 12 }}>
                Complete the ID check on Stripe, then hit Refresh below so we
                pick up the result.
              </p>
              {row.hosted_url && (
                <a
                  href={row.hosted_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-btn pp-btn-primary"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 18px",
                    background: "#F89623",
                    color: "#374751",
                    borderRadius: 999,
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                  data-testid="partner-onboarding-hosted-url"
                >
                  Open Stripe verification
                  <ExternalLink size={16} />
                </a>
              )}
              {inStubMode && (
                <StubHelper
                  onSuccess={async () => {
                    // The stub provider always returns non-terminal on
                    // check_status, so the FE simulates by directly
                    // marking the row as identity_completed via the
                    // pathway that already exists — we call refresh
                    // (which the mock provider returns "not completed"
                    // for) and separately let admin drive it forward.
                    // For manual QA convenience, we surface a hint.
                    setSearchParams({});
                    await refreshStatus();
                  }}
                  onFailure={async () => {
                    setSearchParams({});
                    await refreshStatus();
                  }}
                />
              )}
            </div>
          }
          cta={
            <Button
              variant="outline"
              onClick={refreshStatus}
              disabled={refreshing}
              data-testid="partner-onboarding-refresh-btn"
            >
              {refreshing ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <RefreshCw size={16} style={{ marginRight: 8 }} />
                  I completed my verification
                </>
              )}
            </Button>
          }
        />
      )}

      {row.status === "identity_completed" && (
        <StepCard
          testId="partner-onboarding-step-completed"
          icon={<CheckCircle2 size={22} />}
          title="Identity confirmed. Reviewer next."
          body={
            <p>
              Great — your identity is verified via Stripe. A member of our
              team will manually review your application shortly. You'll get
              an email once we're done.
            </p>
          }
        />
      )}

      {row.status === "admin_in_review" && (
        <StepCard
          testId="partner-onboarding-step-review"
          icon={<Clock size={22} />}
          title="A reviewer is looking at your application now"
          body={
            <p>
              Nothing to do here — sit tight. We aim to finish manual reviews
              within one business day. You'll hear from us by email either
              way.
            </p>
          }
        />
      )}

      {row.status === "needs_info" && (
        <StepCard
          testId="partner-onboarding-step-needs-info"
          icon={<Info size={22} />}
          title="We need a little more info"
          body={
            <div>
              <p style={{ marginBottom: 12 }}>
                Your reviewer left this note:
              </p>
              <blockquote
                style={{
                  margin: 0,
                  padding: "12px 16px",
                  borderLeft: "3px solid #F89623",
                  background: "#FFF8EE",
                  color: "#374751",
                  fontStyle: "italic",
                }}
                data-testid="partner-onboarding-needs-info-message"
              >
                {row.needs_info_message || "Please reach out to support."}
              </blockquote>
            </div>
          }
          cta={
            <Button
              onClick={startVerification}
              disabled={starting}
              data-testid="partner-onboarding-retry-btn"
            >
              {starting ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                "Restart identity check"
              )}
            </Button>
          }
        />
      )}

      {row.status === "approved" && (
        <>
          <StepCard
            testId="partner-onboarding-step-approved"
            icon={<CheckCircle2 size={22} color="#065f46" />}
            title="You're a Verified PooPartner!"
            body={
              <p>
                Identity-verified via Stripe and manually reviewed by our team —
                you're all set. Next step: connect your payout account so we
                can send you money for the bookings you fulfill.
              </p>
            }
          />
          {/* Phase I — Payout onboarding surface. Mounts inline once
              verification is approved. Handles its own load state +
              7-status render tree; see PartnerPayoutSection.jsx for
              the full flow. */}
          <PartnerPayoutSection />
        </>
      )}

      {row.status === "rejected" && (
        <StepCard
          testId="partner-onboarding-step-rejected"
          icon={<XCircle size={22} color="#991b1b" />}
          title="Application not approved this time"
          body={
            <div>
              <p style={{ marginBottom: 12 }}>
                After identity verification via Stripe and manual review by
                our team, we weren't able to move your application forward.
              </p>
              {row.needs_info_message && (
                <blockquote
                  style={{
                    margin: "0 0 12px 0",
                    padding: "12px 16px",
                    borderLeft: "3px solid #991b1b",
                    background: "#fef2f2",
                    color: "#374751",
                    fontStyle: "italic",
                  }}
                  data-testid="partner-onboarding-rejected-reason"
                >
                  {row.needs_info_message}
                </blockquote>
              )}
              <p>
                Think we got this wrong? Reach out — a human reads every
                message.
              </p>
            </div>
          }
          cta={
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="pp-btn pp-btn-ghost"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 18px",
                background: "#ffffff",
                border: "2px solid #374751",
                color: "#374751",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 700,
              }}
              data-testid="partner-onboarding-support-link"
            >
              <Mail size={16} /> Contact support
            </a>
          }
        />
      )}
      {/* One-time post-signup welcome popup for provider users.
          Server-side flag gated (`welcome_popup_seen_at` on
          provider_profile), hidden during admin impersonation.
          The partner-variant email CTA deep-links to
          `/partner/onboarding?welcome=1` — this is a direct-mounted
          route (no <Navigate replace> stripping the query param),
          so `?welcome=1` is preserved by react-router's
          `useSearchParams()` and the popup mounts on first paint.
          See WelcomeToPackPopup + notification_service._welcome_cta_path. */}
      <WelcomeToPackPopup role="provider" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
function StepCard({ testId, icon, title, body, cta }) {
  return (
    <Card
      data-testid={testId}
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <CardHeader>
        <CardTitle
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 18,
            fontWeight: 700,
            color: "#374751",
          }}
        >
          <span style={{ color: "#F89623" }}>{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent style={{ color: "#4b5563", lineHeight: 1.6 }}>
        {body}
        {cta && <div style={{ marginTop: 16 }}>{cta}</div>}
      </CardContent>
    </Card>
  );
}

function StubHelper({ onSuccess, onFailure }) {
  return (
    <div
      style={{
        marginTop: 20,
        padding: 12,
        background: "#F9FAFB",
        border: "1px dashed #d1d5db",
        borderRadius: 12,
      }}
      data-testid="partner-onboarding-stub-helper"
    >
      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 8px 0" }}>
        Stripe stub mode — simulate the outcome:
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          size="sm"
          variant="outline"
          onClick={onSuccess}
          data-testid="partner-onboarding-stub-success-btn"
        >
          Stub success
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onFailure}
          data-testid="partner-onboarding-stub-failure-btn"
        >
          Stub failure
        </Button>
      </div>
    </div>
  );
}
